import { useMemo, useState } from 'react';
import { Upload, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { invalidateTeamAccessQueries } from '@/lib/queryInvalidation';
import { toast } from 'sonner';

interface TeamMembershipImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamNames: string[];
}

type ImportRow = {
  full_name: string;
  department: string;
  email: string;
  role: 'member';
};

type ImportResult = {
  imported_count: number;
  linked_count: number;
  missing_departments: string[] | null;
};

const parseCsvLine = (line: string) => {
  const values: string[] = [];
  let current = '';
  let quoted = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && quoted && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
};

const parseCsv = (text: string): ImportRow[] => {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) throw new Error('CSV sem linhas para importar.');

  const headers = parseCsvLine(lines[0]).map((header) => header.trim().toLowerCase());
  const nameIndex = headers.indexOf('nome completo');
  const departmentIndex = headers.indexOf('departamento');
  const emailIndex = headers.indexOf('email');

  if (nameIndex < 0 || departmentIndex < 0 || emailIndex < 0) {
    throw new Error('CSV precisa conter as colunas Nome completo, Departamento e Email.');
  }

  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    return {
      full_name: values[nameIndex]?.trim() ?? '',
      department: values[departmentIndex]?.trim() ?? '',
      email: values[emailIndex]?.trim().toLowerCase() ?? '',
      role: 'member' as const,
    };
  }).filter((row) => row.email && row.department);
};

export const TeamMembershipImportModal = ({ open, onOpenChange, teamNames }: TeamMembershipImportModalProps) => {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  const knownTeams = useMemo(() => new Set(teamNames.map((name) => name.toLowerCase())), [teamNames]);
  const missingDepartments = useMemo(() => {
    const missing = rows
      .map((row) => row.department)
      .filter((department) => department && !knownTeams.has(department.toLowerCase()));
    return Array.from(new Set(missing)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows, knownTeams]);

  const departments = useMemo(() => new Set(rows.map((row) => row.department)).size, [rows]);
  const canImport = rows.length > 0 && missingDepartments.length === 0 && !importing;

  const reset = () => {
    setRows([]);
    setFileName('');
    setError('');
    setResult(null);
    setImporting(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError('');
    setResult(null);
    setFileName(file.name);

    try {
      const parsed = parseCsv(await file.text());
      if (parsed.length === 0) throw new Error('Nenhum registro válido encontrado no CSV.');
      setRows(parsed);
    } catch (err: any) {
      setRows([]);
      setError(err.message ?? 'Erro ao ler CSV.');
    }
  };

  const handleImport = async () => {
    if (!canImport) return;
    setImporting(true);
    setError('');
    setResult(null);

    const { data, error: rpcError } = await (supabase as any).rpc('import_team_membership_staging', { p_rows: rows });
    setImporting(false);

    if (rpcError) {
      setError(rpcError.message);
      toast.error(`Erro ao importar CSV: ${rpcError.message}`);
      return;
    }

    const importResult = Array.isArray(data) ? data[0] as ImportResult : data as ImportResult;
    const missing = importResult?.missing_departments ?? [];

    if (missing.length > 0) {
      setError(`Departamentos sem time: ${missing.join(', ')}`);
      return;
    }

    setResult(importResult);
    invalidateTeamAccessQueries(queryClient);
    toast.success('Pré-cadastro importado');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Importar membros por CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-color bg-surface p-6 cursor-pointer hover:bg-hover transition-colors">
            <Upload className="h-5 w-5 text-tertiary" />
            <span className="text-sm font-medium text-primary">Selecionar arquivo CSV</span>
            <span className="text-xs text-tertiary">Colunas: Nome completo, Departamento, Email</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(event) => handleFile(event.target.files?.[0])}
            />
          </label>

          {fileName && (
            <div className="rounded-md border border-color bg-secondary px-3 py-2 text-sm text-secondary">
              {fileName}
            </div>
          )}

          {rows.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border border-color p-3">
                <div className="text-lg font-semibold text-primary">{rows.length}</div>
                <div className="text-xs text-tertiary">pessoas</div>
              </div>
              <div className="rounded-md border border-color p-3">
                <div className="text-lg font-semibold text-primary">{departments}</div>
                <div className="text-xs text-tertiary">departamentos</div>
              </div>
              <div className="rounded-md border border-color p-3">
                <div className="text-lg font-semibold text-primary">{missingDepartments.length}</div>
                <div className="text-xs text-tertiary">sem time</div>
              </div>
            </div>
          )}

          {missingDepartments.length > 0 && (
            <div className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>Crie estes times antes de importar: {missingDepartments.join(', ')}</div>
            </div>
          )}

          {error && (
            <div className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {result && (
            <div className="flex gap-2 rounded-md border border-color bg-secondary p-3 text-sm text-secondary">
              <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-brand-primary" />
              <div>
                {result.imported_count} pré-cadastros salvos. {result.linked_count} usuários existentes vinculados agora.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={importing}>
            Fechar
          </Button>
          <Button onClick={handleImport} disabled={!canImport} className="gap-2">
            {importing && <Loader2 className="h-4 w-4 animate-spin" />}
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
